import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import type { IconDefinition } from '@sorare/core/src/atoms/icons';
import { iconGift } from '@sorare/core/src/atoms/icons/system/iconGift.ts';
import { iconPlus } from '@sorare/core/src/atoms/icons/system/iconPlus.ts';
import { iconRefresh } from '@sorare/core/src/atoms/icons/system/iconRefresh.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';

export type LineupsStickyFooterAction = 'restart' | 'claim' | 'setLineup';

type LineupsStickyFooterCallbacks = {
  onRestart: () => void;
  onClaim: () => void;
  onSetLineup: () => void;
};

type LineupsStickyFooterPresentation = {
  icon: IconDefinition;
  label: ReactNode;
  blueBackground?: boolean;
  onClick: () => void;
};

/** Shared icon / label / styling / handler for {@link LineupsStickyFooter} actions. */
export const getLineupsStickyFooterPresentation = (
  action: LineupsStickyFooterAction,
  { onRestart, onClaim, onSetLineup }: LineupsStickyFooterCallbacks
): LineupsStickyFooterPresentation =>
  ({
    restart: {
      icon: iconRefresh,
      label: <FormattedMessage defaultMessage="Try again" />,
      blueBackground: true,
      onClick: onRestart,
    },
    claim: {
      icon: iconGift,
      label: <FormattedMessage defaultMessage="Claim prize" />,
      blueBackground: true,
      onClick: onClaim,
    },
    setLineup: {
      icon: iconPlus,
      label: <FormattedMessage defaultMessage="Add team now" />,
      onClick: onSetLineup,
    },
  })[action];

type Props = LineupsStickyFooterCallbacks & {
  action: LineupsStickyFooterAction;
};

const StickyWrapper = styled(Vertical)`
  position: sticky;
  margin-top: auto;
  width: 100%;
  bottom: 0;
  background-color: var(--background, var(--c-black));
  padding: 0 var(--double-unit) var(--double-unit);
`;

const ButtonWrapper = styled(Horizontal)`
  padding: var(--unit);
  border-radius: 999px;
  margin-top: -30px;
  background-color: var(--background, var(--c-black));
`;

export const LineupsStickyFooter = ({
  action,
  onRestart,
  onClaim,
  onSetLineup,
}: Props) => {
  const presentation = getLineupsStickyFooterPresentation(action, {
    onRestart,
    onClaim,
    onSetLineup,
  });
  return (
    <StickyWrapper
      center
      gap={0.5}
      style={
        presentation.blueBackground
          ? ({ '--background': 'var(--c-blue-400)' } as CSSProperties)
          : undefined
      }
      as={ButtonBase}
      onClick={presentation.onClick}
    >
      <ButtonWrapper>
        <IconButton
          icon={presentation.icon}
          size="large"
          color={presentation.blueBackground ? 'tertiary' : undefined}
          asSpan
        />
      </ButtonWrapper>
      <LabelM bold>{presentation.label}</LabelM>
    </StickyWrapper>
  );
};
